import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, Validators, ReactiveFormsModule } from '@angular/forms';
import {
  FilterDTO,
  FilterOperator,
  IConfigBasedStructuralEntryDTO,
  ParkingSubscriptionClient,
  ParkingSubscriptionDTO,
  ParkingSubscriptionType,
  SortDirection,
} from '../../../../../base/models/models.swagger.generated';
import { DropDownConstantsFactory } from '../../../../../base/shared/syncfusion/dropdown-constants-factory';
import { SubscriptionAssignmentFormGroup } from './edit-parker-edit-subscription-list-form.type';
import { CustomValidatorsFactory } from 'src/app/base/shared/validators/custom-validators-factory';
import { TranslateModule } from '@ngx-translate/core';
import { DateTimePickerModule } from '@syncfusion/ej2-angular-calendars';
import { NgClass } from '@angular/common';
import { YesNoGroupButtonComponent } from '../../../../../base/shared/yes-no-group-button/yes-no-group-button.component';
import { AppErrorMessagesComponent } from '../../../../../base/error-messages/app-error-messages.component';
import { DropDownListComponent, DropDownListModule } from '@syncfusion/ej2-angular-dropdowns';
import { Subscription } from 'rxjs';

export interface IParkingSubscriptionSet extends IConfigBasedStructuralEntryDTO {
  subscriptions: ParkingSubscriptionDTO[];
}

@Component({
    selector: 'app-edit-parker-edit-subscription-list-form',
    templateUrl: './edit-parker-edit-subscription-list-form.component.html',
    styleUrls: ['./edit-parker-edit-subscription-list-form.component.scss'],
    imports: [
        ReactiveFormsModule,
        DropDownListModule,
        AppErrorMessagesComponent,
        YesNoGroupButtonComponent,
        NgClass,
        DateTimePickerModule,
        TranslateModule
    ]
})
export class EditParkerEditSubscriptionListFormComponent implements OnInit, OnDestroy {
  @ViewChild(DropDownListComponent) public dropDownList: DropDownListComponent;

  @Input() public parkerSubscriptionForm: SubscriptionAssignmentFormGroup;
  @Input() public clientId: number;
  @Input() public allowDateSelectionInPast: boolean = false;
  @Input() public useSubscriptionSets: boolean = false;

  public dropdownSettings = DropDownConstantsFactory.createStandardClientDropdownSettings();
  public subscriptionsDropdownValues = [];
  public allSubscriptions: ParkingSubscriptionDTO[] = [];
  public allSubscriptionSets: IParkingSubscriptionSet[] = [];
  public minDate = null;

  private _subscriptions: Subscription = new Subscription();

  constructor(private parkingSubscriptionClient: ParkingSubscriptionClient) {
  }

  public get isAdd(): boolean {
    return !this.parkerSubscriptionForm.value.id;
  }

  public ngOnInit(): void {
    this.minDate = null;
    if (this.isAdd) {
      this.parkerSubscriptionForm.controls.active.setValue(true);
      if (!this.allowDateSelectionInPast) {
        this.minDate = new Date();
      }
    }

    this.handleDateField(this.parkerSubscriptionForm.controls.isValidImmediately.value, this.parkerSubscriptionForm.controls.validFrom, false);
    this.handleDateField(this.parkerSubscriptionForm.controls.isValidOpenEnd.value, this.parkerSubscriptionForm.controls.validTo, true);

    this._subscriptions.add(this.parkerSubscriptionForm.controls.isValidImmediately.valueChanges.subscribe((value) => {
      this.handleDateField(value, this.parkerSubscriptionForm.controls.validFrom, false);
    }));

    this._subscriptions.add(this.parkerSubscriptionForm.controls.isValidOpenEnd.valueChanges.subscribe((value) => {
      this.handleDateField(value, this.parkerSubscriptionForm.controls.validTo, true);
    }));

    this._subscriptions.add(this.parkerSubscriptionForm.controls.subscriptionId.valueChanges.subscribe((value) => {
      if (this.useSubscriptionSets) {
        const set = this.allSubscriptionSets.find(s => s.id === value);
        this.parkerSubscriptionForm.controls.selectedSubscriptions.setValue(set?.subscriptions ?? []);
      } else {
        this.parkerSubscriptionForm.controls.selectedSubscriptions.setValue(this.allSubscriptions.filter(sub => sub.id === value));
      }
    }));

    const filters = [
      new FilterDTO({
        isComplex: false,
        field: 'Active',
        operator: FilterOperator.Equal,
        value: 'true'
      }),
      new FilterDTO({
        isComplex: false,
        field: 'Assignable',
        operator: FilterOperator.Equal,
        value: 'true',
      }),
      new FilterDTO({
        isComplex: false,
        field: 'Type',
        operator: FilterOperator.NotEqual,
        value: ParkingSubscriptionType.ForeignParkerSubscription.toString()
      })
    ];

    this.parkingSubscriptionClient.searchParkingSubscriptions(this.clientId, filters, 0, 0, 'Name', SortDirection.Ascending)
    .subscribe(res => {
      this.allSubscriptions = res.items;

      if (this.isAdd && this.useSubscriptionSets) {
        this.allSubscriptionSets = this.createSubscriptionSetsFromSubscriptions(this.allSubscriptions);

        this.subscriptionsDropdownValues = this.allSubscriptionSets.map(s => {
          return {name: s.name, id: s.id};
        });
        this.dropDownList.refresh();
        return;
      }

      // when subscription is not valid, remove value and show error
      if (!this.allSubscriptions.find(sub => sub.id === this.parkerSubscriptionForm.controls.subscriptionId.value) && !this.isAdd) {
        this.parkerSubscriptionForm.controls.subscriptionId.setValue(null);
        this.parkerSubscriptionForm.controls.subscriptionId.setErrors({required: true});
        this.parkerSubscriptionForm.controls.subscriptionId.markAsTouched();
      }

      this.subscriptionsDropdownValues = res.items.map(s => {
        let label = s.name;
        if (s.description) {
          label += ', ' + s.description;
        }

        return {name: label, id: s.id};
      });
    });
  }

  public ngOnDestroy(): void {
    this._subscriptions.unsubscribe();
  }

  public handleDateField(disable: boolean, control: AbstractControl, includeDateBeforeValidator: boolean): void {
    if (disable) {
      control.disable();
      control.setValue(null, {emitEvent: false});
      control.setValidators([]);
    } else {
      control.enable();
      const validators = [Validators.required];
      if (this.parkerSubscriptionForm.controls.id.value <= 0 && !this.allowDateSelectionInPast) {
        validators.push(CustomValidatorsFactory.validateDateNotInPast());
      }
      if (includeDateBeforeValidator) {
        validators.push(CustomValidatorsFactory.validateDateNotBeforeDate(this.parkerSubscriptionForm.controls.validFrom));
      }
      control.setValidators(validators);
    }

    control.updateValueAndValidity();
  }

  private createSubscriptionSetsFromSubscriptions(subscriptions: ParkingSubscriptionDTO[]): IParkingSubscriptionSet[] {
    const structuralEntryMap = new Map<number, IParkingSubscriptionSet>();
    subscriptions.forEach(sub => {
      sub.structuralEntries?.forEach(entry => {
        if (!structuralEntryMap.has(entry.id)) {
          structuralEntryMap.set(entry.id, { ...entry, subscriptions: [] });
        }
        structuralEntryMap.get(entry.id).subscriptions.push(sub);
      });
    });
    const structuralEntries = Array.from(structuralEntryMap.values());

    // we iterate through all subscription sets and add subscriptions to the parents as well by still using the map
    structuralEntries.forEach(e => {
      const subscriptions = e.subscriptions;
      let entry = e;
      while (entry?.parentId && structuralEntryMap.has(entry.parentId)) {
        entry = structuralEntryMap.get(entry.parentId);
        entry.subscriptions.push(...subscriptions);
        // Remove duplicate subscriptions
        const uniqueSubsMap = new Map<number, any>();
        entry.subscriptions.forEach(sub => {
          uniqueSubsMap.set(sub.id, sub);
        });
        entry.subscriptions = Array.from(uniqueSubsMap.values());
        entry.subscriptions = entry.subscriptions.sort((a, b) => a.name.localeCompare(b.name));
      }
    });

    return structuralEntries.filter(e => e.isSubscriptionSet).sort((a, b) => a.name.localeCompare(b.name));
  }

}
